import { NSARouterMakerResult } from "namirasoft-account-react";
import { RouterRouteCcgType, RouterRouteInferencerType, RouterRouteInputRow } from "namirasoft-inference";
import { IBaseComponentProps, NSBox, NSBoxBoolean, NSBoxInteger, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";
import CCGTypeID from "./CCGTypeID";
import InferencerTypeID from "./InferencerTypeID";

export interface RouterRouteLineProps extends IBaseComponentProps, NSARouterMakerResult, InferenceRouterProps
{
    index: number;
    deletable: boolean;
    onDelete: () => void;
    onChanged?: (value: RouterRouteInputRow | null) => void;
}

export default class RouterRouteLine extends Component<RouterRouteLineProps>
{
    static IS_DIRTY(value: RouterRouteInputRow | null): boolean
    {
        if (value && (value.ccg_id || value.inferencer_id))
            return true;
        return false;
    }

    private NSBoxInteger_Order = createRef<NSBoxInteger>();
    private CCGTypeID_Ccg = createRef<CCGTypeID>();
    private NSBoxBoolean_CcgNot = createRef<NSBoxBoolean>();
    private InferencerTypeID_Inferencer = createRef<InferencerTypeID<RouterRouteInferencerType>>();

    constructor(props: RouterRouteLineProps)
    {
        super(props);
        this.getValue = this.getValue.bind(this);
        this.isDirty = this.isDirty.bind(this);
        this.onChanged = this.onChanged.bind(this);
    }

    getValue(checkError: boolean = false): RouterRouteInputRow | null
    {
        let order = this.NSBoxInteger_Order.current?.getValue(checkError) ?? 0;
        let ccg = this.CCGTypeID_Ccg.current?.getValue(checkError) ?? { ccg_type: RouterRouteCcgType.Condition, ccg_id: "" };
        let ccg_type = ccg.ccg_type;
        let ccg_id = ccg.ccg_id;
        let ccg_not = this.NSBoxBoolean_CcgNot.current?.getValue() ?? false;
        let inferencer = this.InferencerTypeID_Inferencer.current?.getValue(checkError) ?? { inferencer_type: RouterRouteInferencerType.MODEL, inferencer_id: "" };
        let inferencer_type = inferencer.inferencer_type;
        let inferencer_id = inferencer.inferencer_id;
        if (!ccg_id || !inferencer_id)
            return null;
        return { order: order ?? 0, ccg_type, ccg_id, ccg_not, inferencer_type, inferencer_id };
    }

    setValue(value: RouterRouteInputRow)
    {
        this.NSBoxInteger_Order.current?.setValue(value.order);
        this.CCGTypeID_Ccg.current?.setValue({ ccg_type: value.ccg_type, ccg_id: value.ccg_id });
        this.NSBoxBoolean_CcgNot.current?.setValue(value.ccg_not);
        this.InferencerTypeID_Inferencer.current?.setValue({ inferencer_type: value.inferencer_type, inferencer_id: value.inferencer_id });
    }

    isDirty(): boolean
    {
        let ccg = this.CCGTypeID_Ccg.current?.getValue(false) ?? { ccg_type: RouterRouteCcgType.Condition, ccg_id: "" };
        let inferencer = this.InferencerTypeID_Inferencer.current?.getValue(false) ?? { inferencer_type: RouterRouteInferencerType.MODEL, inferencer_id: "" };
        return !!(ccg.ccg_id || inferencer.inferencer_id);
    }

    private onChanged()
    {
        if (this.props.onChanged)
            this.props.onChanged(this.getValue(false));
    }

    override render()
    {
        return (
            <NSColumn style={{ ...this.props.style }}>
                <NSRow style={{ flexWrap: "nowrap" }}>
                    <CCGTypeID
                        ref={this.CCGTypeID_Ccg}
                        {...this.props}
                        typeTitle="If CCG Type"
                        idTitle={`If CCG ${this.props.index + 1}`}
                        required
                        width={NSBox.width.one}
                        onChanged={this.onChanged}
                        info={{ link: "https://namirasoft.com/inference/console-guide/router/#field-routes" }}
                    />
                    <NSBoxBoolean
                        ref={this.NSBoxBoolean_CcgNot}
                        title="Match when the CCG result is false"
                        headerTitle="Not"
                        required={false}
                        onChanged={this.onChanged}
                        info={{ link: "https://namirasoft.com/inference/console-guide/router/#field-routes" }}
                    />
                </NSRow>
                <NSRow style={{ flexWrap: "nowrap" }}>
                    <InferencerTypeID<RouterRouteInferencerType>
                        ref={this.InferencerTypeID_Inferencer}
                        {...this.props}
                        typeTitle="Then Inferencer Type"
                        idTitle="Then Inferencer"
                        required
                        width={NSBox.width.one}
                        getEnumObject={() => RouterRouteInferencerType}
                        defaultType={RouterRouteInferencerType.MODEL}
                        onChanged={this.onChanged}
                        info={{ link: "https://namirasoft.com/inference/console-guide/router/#field-routes" }}
                    />
                    <NSBoxInteger
                        ref={this.NSBoxInteger_Order}
                        title="Order"
                        required
                        min={0}
                        style={{ width: NSBox.width.one, minWidth: NSBox.width.one, flexShrink: 0 }}
                        onChanged={this.onChanged}
                        info={{ link: "https://namirasoft.com/inference/console-guide/router/#field-routes" }}
                        menu={{
                            builtin: { delete: { enabled: this.props.deletable, onDelete: () => { this.props.onDelete(); } } },
                            items: []
                        }}
                    />
                </NSRow>
            </NSColumn>
        );
    }
}